import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Menu, 
  X, 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  ChefHat, 
  Users, 
  Lightbulb, 
  TrendingUp, 
  FileText, 
  Award,
  Shield,
  GraduationCap,
  Wine,
  Heart,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Star,
  Play,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL from user-provided asset
const LOGO_URL = "https://customer-assets.emergentagent.com/job_e9c33c6a-af4f-4988-bb48-d1ed87b9676f/artifacts/06bqp001_1.png";
const PHONE_NUMBER = "845-208-9725";
const EMAIL = "a2hospitalityandconsultingserv@gmail.com";

// Video URLs
const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_food-cert-experts/artifacts/x1vwpbbm_8625856-uhd_3840_2160_25fps.mp4";
const VIDEO_2 = "https://customer-assets.emergentagent.com/job_food-cert-experts/artifacts/z7plzpu8_8477339-hd_1080_1920_24fps.mp4";
const VIDEO_3 = "https://customer-assets.emergentagent.com/job_food-cert-experts/artifacts/wqou5oub_8902312-hd_1920_1080_25fps.mp4";
const VIDEO_4 = "https://customer-assets.emergentagent.com/job_food-cert-experts/artifacts/2mtpvib1_8094254-hd_1920_1080_25fps.mp4";
const VIDEO_5 = "https://customer-assets.emergentagent.com/job_food-cert-experts/artifacts/0gzvmtfe_3338747-uhd_4096_2160_25fps.mp4";
// New videos
const VIDEO_6 = "https://customer-assets.emergentagent.com/job_food-cert-experts/artifacts/gcgj4j41_8094254-hd_1920_1080_25fps.mp4";
const VIDEO_7 = "https://customer-assets.emergentagent.com/job_food-cert-experts/artifacts/hydo3pl6_8477339-hd_1080_1920_24fps.mp4";
const VIDEO_8 = "https://customer-assets.emergentagent.com/job_food-cert-experts/artifacts/0q8hgh4w_8902312-hd_1920_1080_25fps.mp4";

// Image URLs
const IMAGES = {
  chef1: "https://images.unsplash.com/photo-1622021142947-da7dedc7c39a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBjaGVmJTIwa2l0Y2hlbnxlbnwwfHx8fDE3Njg0MjgzODJ8MA&ixlib=rb-4.1.0&q=85&w=800",
  chef2: "https://images.unsplash.com/photo-1625489539789-39bb40ed9a8a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjaGVmJTIwa2l0Y2hlbnxlbnwwfHx8fDE3Njg0MjgzODJ8MA&ixlib=rb-4.1.0&q=85&w=800",
  chef3: "https://images.unsplash.com/photo-1592498546551-222538011a27?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBjaGVmJTIwa2l0Y2hlbnxlbnwwfHx8fDE3Njg0MjgzODJ8MA&ixlib=rb-4.1.0&q=85&w=800",
  training1: "https://images.unsplash.com/photo-1578366941741-9e517759c620?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxmb29kJTIwc2FmZXR5JTIwdHJhaW5pbmd8ZW58MHx8fHwxNzY4NDI4MzkxfDA&ixlib=rb-4.1.0&q=85&w=800",
  training2: "https://images.unsplash.com/photo-1617448570768-09ae7a069496?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxmb29kJTIwc2FmZXR5JTIwdHJhaW5pbmd8ZW58MHx8fHwxNzY4NDI4MzkxfDA&ixlib=rb-4.1.0&q=85&w=800",
  meeting: "https://images.unsplash.com/photo-1539056276907-dc946d5098c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxyZXN0YXVyYW50JTIwbWVldGluZ3xlbnwwfHx8fDE3Njg0Mjg0MDR8MA&ixlib=rb-4.1.0&q=85&w=800",
  consulting: "https://images.unsplash.com/photo-1713804459612-485847ba0be2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxob3NwaXRhbGl0eSUyMGNvbnN1bHRpbmd8ZW58MHx8fHwxNzY4NDI4Mzk3fDA&ixlib=rb-4.1.0&q=85&w=800"
};

// ============================================
// 3D FLOATING ELEMENTS COMPONENT
// ============================================
const FloatingElements = () => {
  return (
    <div className="floating-elements pointer-events-none">
      <div className="floating-element floating-1">
        <Sparkles className="w-8 h-8 text-[#C9A227]/30" />
      </div>
      <div className="floating-element floating-2">
        <Star className="w-6 h-6 text-[#C9A227]/20" />
      </div>
      <div className="floating-element floating-3">
        <Award className="w-10 h-10 text-[#C9A227]/25" />
      </div>
      <div className="floating-element floating-4">
        <ChefHat className="w-12 h-12 text-[#C9A227]/20" />
      </div>
    </div>
  );
};

// ============================================
// 3D CARD COMPONENT
// ============================================
const Card3D = ({ children, className = "" }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;
    
    cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
  };

  return (
    <div
      ref={cardRef}
      className={`card-3d ${className}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>
  );
};

// ============================================
// HEADER COMPONENT
// ============================================
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Training', path: '/training' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-md shadow-lg shadow-[#C9A227]/10' : 'bg-transparent'
      }`}
      data-testid="header"
    >
      {/* Top bar with phone */}
      <div className="bg-gradient-to-r from-[#C9A227] via-[#E5C76B] to-[#C9A227] py-1 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <a 
            href={`tel:${PHONE_NUMBER}`} 
            className="flex items-center gap-2 text-black font-medium hover:opacity-80 transition-opacity"
            data-testid="header-phone-link"
            aria-label={`Call us at ${PHONE_NUMBER}`}
          >
            <Phone className="w-4 h-4" />
            <span>{PHONE_NUMBER}</span>
          </a>
          <a 
            href={`mailto:${EMAIL}`}
            className="flex items-center gap-2 text-black font-medium hover:opacity-80 transition-opacity"
            aria-label={`Email us at ${EMAIL}`}
          >
            <Mail className="w-4 h-4" />
            <span className="hidden sm:inline">{EMAIL}</span>
          </a>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center transform hover:scale-105 transition-transform" data-testid="logo-link">
          <img 
            src={LOGO_URL} 
            alt="A2 Hospitality & Consulting Services LLC Logo" 
            className="h-16 w-auto drop-shadow-[0_0_15px_rgba(201,162,39,0.3)]"
            loading="eager"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-link text-sm font-medium transition-all duration-300 hover:text-[#C9A227] hover:drop-shadow-[0_0_10px_rgba(201,162,39,0.5)] ${
                isActive(link.path) ? 'text-[#C9A227] active' : 'text-white'
              }`}
              data-testid={`nav-${link.name.toLowerCase()}`}
            >
              {link.name}
            </Link>
          ))}
          <a
            href={`tel:${PHONE_NUMBER}`}
            className="cta-button px-4 py-2 rounded-md text-sm flex items-center gap-2 shadow-lg shadow-[#C9A227]/30 hover:shadow-[#C9A227]/50 transition-all"
            data-testid="header-cta-call"
          >
            <Phone className="w-4 h-4" />
            Call Now
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          data-testid="mobile-menu-button"
        >
          {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden mobile-menu absolute top-full left-0 right-0 py-4 px-4 border-t border-[#C9A227]/20">
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-lg font-medium py-2 ${
                  isActive(link.path) ? 'text-[#C9A227]' : 'text-white'
                }`}
                onClick={() => setIsMenuOpen(false)}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
            <a
              href={`tel:${PHONE_NUMBER}`}
              className="cta-button px-4 py-3 rounded-md text-center flex items-center justify-center gap-2 mt-2"
              data-testid="mobile-cta-call"
            >
              <Phone className="w-5 h-5" />
              Call {PHONE_NUMBER}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

// ============================================
// FOOTER COMPONENT
// ============================================
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0f0f0f] border-t border-[#C9A227]/20 relative overflow-hidden" data-testid="footer">
      {/* 3D Background effect */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#C9A227] rounded-full blur-[150px]"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#C9A227] rounded-full blur-[150px]"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo and description */}
          <div className="md:col-span-1">
            <img 
              src={LOGO_URL} 
              alt="A2 Hospitality & Consulting Services LLC" 
              className="h-20 w-auto mb-4 drop-shadow-[0_0_20px_rgba(201,162,39,0.3)]"
              loading="lazy"
            />
            <p className="text-gray-400 text-sm">
              Elevating culinary excellence and hospitality standards through expert consulting and professional training.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-[#C9A227] font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="footer-link text-sm">Home</Link></li>
              <li><Link to="/services" className="footer-link text-sm">Services</Link></li>
              <li><Link to="/training" className="footer-link text-sm">Training & Certifications</Link></li>
              <li><Link to="/about" className="footer-link text-sm">About Us</Link></li>
              <li><Link to="/contact" className="footer-link text-sm">Contact</Link></li>
            </ul>
          </div>

          {/* Training Programs */}
          <div>
            <h3 className="text-[#C9A227] font-semibold mb-4">Training Programs</h3>
            <ul className="space-y-2">
              <li><Link to="/training" className="footer-link text-sm">ServSafe Food Handler</Link></li>
              <li><Link to="/training" className="footer-link text-sm">ServSafe Manager</Link></li>
              <li><Link to="/training" className="footer-link text-sm">Allergen Awareness</Link></li>
              <li><Link to="/training" className="footer-link text-sm">CPR Certification</Link></li>
              <li><Link to="/training" className="footer-link text-sm">All Certifications</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-[#C9A227] font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href={`tel:${PHONE_NUMBER}`} 
                  className="footer-link text-sm flex items-center gap-2"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 text-[#C9A227]" />
                  {PHONE_NUMBER}
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${EMAIL}`} 
                  className="footer-link text-sm flex items-center gap-2"
                >
                  <Mail className="w-4 h-4 text-[#C9A227]" />
                  {EMAIL}
                </a>
              </li>
              <li className="flex items-start gap-2 text-gray-400 text-sm">
                <MapPin className="w-4 h-4 text-[#C9A227] mt-0.5" />
                <span>Serving clients nationwide</span>
              </li>
              <li className="flex items-start gap-2 text-gray-400 text-sm">
                <Clock className="w-4 h-4 text-[#C9A227] mt-0.5" />
                <div>
                  <p>Monday - Sunday: 8:00 AM - 5:00 PM</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[#C9A227]/20 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {currentYear} A2 Hospitality & Consulting Services LLC. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="text-gray-500 text-sm hover:text-[#C9A227] transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============================================
// HOME PAGE
// ============================================
const HomePage = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(err => console.log('Video autoplay prevented:', err));
    }
  }, []);

  // Services data
  const services = [
    {
      icon: ChefHat,
      title: "Culinary Consulting",
      description: "Expert guidance on menu development, kitchen operations, and culinary innovation to elevate your food offerings.",
      image: IMAGES.chef1
    },
    {
      icon: Users,
      title: "Staff Training",
      description: "Comprehensive training programs to enhance service excellence, food safety, and professional development.",
      image: IMAGES.training1
    },
    {
      icon: Lightbulb,
      title: "Concept Development",
      description: "Transform your vision into reality with strategic planning and creative concept development.",
      image: IMAGES.chef2
    },
    {
      icon: TrendingUp,
      title: "Business Strategy",
      description: "Optimize operations, increase profitability, and position your establishment for sustainable growth.",
      image: IMAGES.meeting
    },
    {
      icon: FileText,
      title: "Menu Engineering",
      description: "Data-driven menu analysis and design to maximize profitability while delighting guests.",
      image: IMAGES.chef3
    },
    {
      icon: Award,
      title: "Quality Assurance",
      description: "Implement industry best practices and standards to ensure consistent excellence.",
      image: IMAGES.consulting
    }
  ];

  // Training programs quick list
  const trainingPrograms = [
    "ServSafe Food Handler",
    "ServSafe Food Protection Manager",
    "Allergen Awareness Training",
    "Alcohol Training",
    "Sexual Harassment Prevention",
    "CPR Certification",
    "AHEIL Customer Service Certified"
  ];

  return (
    <main data-testid="home-page">
      <Helmet>
        <title>A2 Hospitality & Consulting Services LLC | Premium Hospitality Consulting</title>
        <meta name="description" content="Elevating culinary excellence and hospitality standards through expert consulting, staff training, ServSafe certifications, and strategic business solutions." />
        <link rel="canonical" href="https://a2consulting.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="A2 Hospitality & Consulting Services LLC | Premium Hospitality Consulting" />
        <meta property="og:description" content="Expert consulting, professional training, and strategic business solutions for restaurants, hotels, and food service operations." />
        <meta property="og:url" content="https://a2consulting.com/" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_e9c33c6a-af4f-4988-bb48-d1ed87b9676f/artifacts/06bqp001_1.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="A2 Hospitality & Consulting Services LLC | Premium Hospitality Consulting" />
        <meta name="twitter:description" content="Expert consulting, professional training, and strategic business solutions for restaurants, hotels, and food service operations." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_e9c33c6a-af4f-4988-bb48-d1ed87b9676f/artifacts/06bqp001_1.png" />
      </Helmet>
      
      {/* Skip to main content for accessibility */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            poster={IMAGES.chef1}
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
          {/* Video Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-[#0a0a0a]"></div>
        </div>

        {/* Floating 3D Elements */}
        <FloatingElements />
        
        {/* 3D Glow Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#C9A227]/20 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#C9A227]/10 rounded-full blur-[120px] animate-pulse delay-1000"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center pt-32" id="main-content">
          <div className="animate-fade-in">
            <img 
              src={LOGO_URL} 
              alt="A2 Hospitality & Consulting Services LLC" 
              className="h-32 md:h-44 w-auto mx-auto mb-8 drop-shadow-[0_0_30px_rgba(201,162,39,0.5)] hover:drop-shadow-[0_0_50px_rgba(201,162,39,0.7)] transition-all duration-500 transform hover:scale-105"
              loading="eager"
            />
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Elevating <span className="text-gold-gradient drop-shadow-[0_0_20px_rgba(201,162,39,0.5)]">Culinary Excellence</span>
              <br />& Hospitality Standards
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Expert consulting, professional training, and strategic business solutions for restaurants, hotels, and food service operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/services')}
                className="cta-button px-8 py-6 text-lg rounded-md shadow-lg shadow-[#C9A227]/40 hover:shadow-[#C9A227]/60 transform hover:scale-105 transition-all duration-300"
                data-testid="hero-services-btn"
              >
                Our Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                onClick={() => navigate('/contact')}
                className="outline-button px-8 py-6 text-lg rounded-md transform hover:scale-105 transition-all duration-300"
                data-testid="hero-contact-btn"
              >
                Contact Us
              </Button>
            </div>
          </div>
          
          {/* Scroll indicator */}
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-[#C9A227]/50 rounded-full flex justify-center pt-2">
              <div className="w-1 h-3 bg-[#C9A227] rounded-full animate-scroll"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section with 3D Cards */}
      <section className="py-20 px-4 relative overflow-hidden" data-testid="services-section">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C9A227]/5 rounded-full blur-[150px]"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#C9A227]/5 rounded-full blur-[120px]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <p className="text-[#C9A227] uppercase tracking-wider text-sm font-medium mb-2 flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4" />
              What We Offer
              <Sparkles className="w-4 h-4" />
            </p>
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Comprehensive hospitality and consulting solutions tailored to elevate your establishment to new heights of excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card3D key={index}>
                <Card 
                  className="service-card bg-[#1a1a1a] border-[#C9A227]/20 overflow-hidden h-full"
                  data-testid={`service-card-${index}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] to-transparent"></div>
                    <div className="absolute bottom-4 left-4 w-12 h-12 rounded-lg bg-[#C9A227]/90 flex items-center justify-center shadow-lg shadow-[#C9A227]/30">
                      <service.icon className="w-6 h-6 text-black" />
                    </div>
                  </div>
                  <CardHeader className="pt-4">
                    <CardTitle className="text-xl font-semibold text-white">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400">{service.description}</CardDescription>
                  </CardContent>
                </Card>
              </Card3D>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => navigate('/services')}
              className="outline-button px-6 py-3 transform hover:scale-105 transition-all"
              data-testid="view-all-services-btn"
            >
              View All Services
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Training Section with Image */}
      <section className="py-20 px-4 relative overflow-hidden" data-testid="training-preview-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-[#C9A227]/5 rounded-full blur-[200px] transform -translate-y-1/2"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#C9A227] uppercase tracking-wider text-sm font-medium mb-2">Professional Development</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">Training & Certifications</h2>
              <p className="text-gray-400 mb-6">
                As certified ServSafe Proctors and Instructors, we offer comprehensive training programs to ensure your team meets the highest industry standards.
              </p>
              <ul className="space-y-3 mb-8">
                {trainingPrograms.map((program, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-300 group">
                    <div className="w-6 h-6 rounded-full bg-[#C9A227]/20 flex items-center justify-center group-hover:bg-[#C9A227]/40 transition-colors">
                      <CheckCircle className="w-4 h-4 text-[#C9A227]" />
                    </div>
                    <span className="group-hover:text-[#C9A227] transition-colors">{program}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/training')}
                  className="cta-button px-6 py-3 shadow-lg shadow-[#C9A227]/30"
                  data-testid="request-training-btn"
                >
                  Request Training
                </Button>
                <a
                  href={`tel:${PHONE_NUMBER}`}
                  className="outline-button px-6 py-3 rounded-md flex items-center justify-center gap-2"
                  data-testid="call-training-btn"
                >
                  <Phone className="w-4 h-4" />
                  Call {PHONE_NUMBER}
                </a>
              </div>
            </div>
            <div className="relative">
              <Card3D>
                <div className="relative rounded-xl overflow-hidden border border-[#C9A227]/30 shadow-2xl shadow-[#C9A227]/20">
                  <img 
                    src={IMAGES.training2} 
                    alt="Professional food safety training session" 
                    className="w-full h-auto"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
              </Card3D>
              <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-[#C9A227] to-[#E5C76B] text-black p-4 rounded-lg shadow-xl transform hover:scale-105 transition-transform">
                <p className="font-bold text-lg">Certified</p>
                <p className="text-sm">ServSafe Proctors</p>
              </div>
              {/* Floating badge */}
              <div className="absolute -top-4 -left-4 bg-black border border-[#C9A227] p-3 rounded-lg shadow-xl animate-float">
                <GraduationCap className="w-8 h-8 text-[#C9A227]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 px-4 relative overflow-hidden" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Star, title: "Expert", subtitle: "Industry Leaders" },
                  { icon: Award, title: "Certified", subtitle: "ServSafe Instructors" },
                  { icon: Users, title: "Dedicated", subtitle: "Client Success" },
                  { icon: MapPin, title: "Nationwide", subtitle: "Service Coverage" }
                ].map((item, index) => (
                  <Card3D key={index}>
                    <div className="bg-[#1a1a1a] p-6 rounded-xl border border-[#C9A227]/20 hover:border-[#C9A227]/50 transition-all h-full">
                      <item.icon className="w-8 h-8 text-[#C9A227] mb-2" />
                      <p className="text-2xl font-bold text-white">{item.title}</p>
                      <p className="text-gray-400 text-sm">{item.subtitle}</p>
                    </div>
                  </Card3D>
                ))}
              </div>
            </div>
            <div className="order-1 md:order-2">
              <p className="text-[#C9A227] uppercase tracking-wider text-sm font-medium mb-2">About Us</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">Crafting Excellence in Hospitality</h2>
              <p className="text-gray-400 mb-4">
                A2 Hospitality & Consulting Services LLC is dedicated to transforming hospitality establishments through expert guidance, innovative strategies, and a passion for culinary excellence.
              </p>
              <p className="text-gray-400 mb-6">
                We partner with restaurants, hotels, and food service operations to deliver exceptional results. Our team brings decades of combined experience in the hospitality industry.
              </p>
              <Button 
                onClick={() => navigate('/about')}
                className="outline-button px-6 py-3"
                data-testid="learn-more-about-btn"
              >
                Learn More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with 3D Effect */}
      <section className="py-20 px-4 relative overflow-hidden" data-testid="cta-section">
        {/* 3D Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-[#C9A227]/20 via-[#C9A227]/10 to-[#C9A227]/20"></div>
          <div className="absolute top-0 left-1/2 w-[800px] h-[800px] bg-[#C9A227]/10 rounded-full blur-[200px] transform -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-[#C9A227]/10 px-4 py-2 rounded-full mb-6 border border-[#C9A227]/30">
            <Sparkles className="w-5 h-5 text-[#C9A227]" />
            <span className="text-[#C9A227] font-medium">Ready to Transform Your Business?</span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">Ready to Elevate Your Business?</h2>
          <p className="text-gray-400 mb-8 text-lg">
            Contact us today to discuss how we can help you achieve excellence in hospitality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/contact')}
              className="cta-button px-8 py-4 text-lg shadow-xl shadow-[#C9A227]/40 transform hover:scale-105 transition-all"
              data-testid="cta-contact-btn"
            >
              Contact Us
            </Button>
            <a
              href={`tel:${PHONE_NUMBER}`}
              className="outline-button px-8 py-4 text-lg rounded-md flex items-center justify-center gap-2 transform hover:scale-105 transition-all"
              data-testid="cta-call-btn"
            >
              <Phone className="w-5 h-5" />
              {PHONE_NUMBER}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

// ============================================
// SERVICES PAGE
// ============================================
const ServicesPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: ChefHat,
      title: "Culinary Consulting",
      description: "Expert guidance on menu development, kitchen operations, and culinary innovation to elevate your food offerings.",
      features: ["Menu Development", "Recipe Creation", "Kitchen Workflow", "Cost Analysis"],
      image: IMAGES.chef1
    },
    {
      icon: Users,
      title: "Staff Training",
      description: "Comprehensive training programs to enhance service excellence, food safety, and professional development.",
      features: ["Service Excellence", "Food Safety", "Team Building", "Leadership Development"],
      image: IMAGES.training1
    },
    {
      icon: Lightbulb,
      title: "Concept Development",
      description: "Transform your vision into reality with strategic planning and creative concept development.",
      features: ["Brand Identity", "Market Research", "Design Planning", "Launch Strategy"],
      image: IMAGES.chef2
    },
    {
      icon: TrendingUp,
      title: "Business Strategy",
      description: "Optimize operations, increase profitability, and position your establishment for sustainable growth.",
      features: ["Financial Planning", "Operations Audit", "Growth Strategy", "Performance Metrics"],
      image: IMAGES.meeting
    },
    {
      icon: FileText,
      title: "Menu Engineering",
      description: "Data-driven menu analysis and design to maximize profitability while delighting guests.",
      features: ["Pricing Strategy", "Menu Layout", "Item Analysis", "Seasonal Planning"],
      image: IMAGES.chef3
    },
    {
      icon: Award,
      title: "Quality Assurance",
      description: "Implement industry best practices and standards to ensure consistent excellence.",
      features: ["Standards Implementation", "Compliance Audits", "Quality Control", "Certification Support"],
      image: IMAGES.consulting
    }
  ];

  return (
    <main className="pt-32" data-testid="services-page">
      <Helmet>
        <title>Our Services | A2 Hospitality & Consulting Services LLC</title>
        <meta name="description" content="Comprehensive hospitality consulting services including culinary consulting, staff training, concept development, business strategy, menu engineering, and quality assurance." />
        <link rel="canonical" href="https://a2consulting.com/services" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Our Services | A2 Hospitality & Consulting Services LLC" />
        <meta property="og:description" content="Comprehensive hospitality and consulting solutions tailored to elevate your establishment to new heights of excellence." />
        <meta property="og:url" content="https://a2consulting.com/services" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_e9c33c6a-af4f-4988-bb48-d1ed87b9676f/artifacts/06bqp001_1.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Our Services | A2 Hospitality & Consulting Services LLC" />
        <meta name="twitter:description" content="Comprehensive hospitality and consulting solutions tailored to elevate your establishment to new heights of excellence." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_e9c33c6a-af4f-4988-bb48-d1ed87b9676f/artifacts/06bqp001_1.png" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C9A227]/10 rounded-full blur-[150px]"></div>
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Our <span className="text-gold-gradient">Services</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Comprehensive hospitality and consulting solutions tailored to elevate your establishment to new heights of excellence.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card3D key={index}>
                <Card 
                  className="service-card bg-[#1a1a1a] border-[#C9A227]/20 overflow-hidden"
                  data-testid={`service-detail-card-${index}`}
                >
                  <div className="grid md:grid-cols-2">
                    <div className="relative h-48 md:h-full">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#1a1a1a] md:block hidden"></div>
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] to-transparent md:hidden"></div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-[#C9A227]/10 flex items-center justify-center flex-shrink-0">
                          <service.icon className="w-6 h-6 text-[#C9A227]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-white">{service.title}</h3>
                        </div>
                      </div>
                      <p className="text-gray-400 mb-4">{service.description}</p>
                      <div className="border-t border-[#C9A227]/10 pt-4">
                        <p className="text-sm text-[#C9A227] font-medium mb-3">Key Features:</p>
                        <ul className="grid grid-cols-2 gap-2">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                              <CheckCircle className="w-4 h-4 text-[#C9A227]" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </Card>
              </Card3D>
            ))}
          </div>
        </div>
      </section>

      {/* Video Showcase Section */}
      <section className="py-20 px-4 bg-[#0f0f0f] relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#C9A227] uppercase tracking-wider text-sm font-medium mb-2">See Us in Action</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Culinary Excellence</h2>
            <p className="text-gray-400">Experience the artistry and precision we bring to every project</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card3D className="aspect-video">
              <div className="relative w-full h-full rounded-xl overflow-hidden border border-[#C9A227]/30 shadow-xl shadow-[#C9A227]/10">
                <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                  <source src={VIDEO_3} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-semibold">Kitchen Operations</p>
                  <p className="text-gray-300 text-sm">Professional Standards</p>
                </div>
              </div>
            </Card3D>
            
            <Card3D className="aspect-video">
              <div className="relative w-full h-full rounded-xl overflow-hidden border border-[#C9A227]/30 shadow-xl shadow-[#C9A227]/10">
                <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                  <source src={VIDEO_4} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-semibold">Fine Dining</p>
                  <p className="text-gray-300 text-sm">Memorable Experiences</p>
                </div>
              </div>
            </Card3D>
            
            <Card3D className="aspect-video">
              <div className="relative w-full h-full rounded-xl overflow-hidden border border-[#C9A227]/30 shadow-xl shadow-[#C9A227]/10">
                <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                  <source src={VIDEO_5} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-semibold">Culinary Artistry</p>
                  <p className="text-gray-300 text-sm">Passion Meets Precision</p>
                </div>
              </div>
            </Card3D>
          </div>
        </div>
      </section>

      {/* Training CTA */}
      <section className="py-20 px-4 bg-[#0f0f0f] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 w-[600px] h-[600px] bg-[#C9A227]/10 rounded-full blur-[200px] transform -translate-x-1/2"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
            Looking for <span className="text-gold-gradient">Training & Certifications</span>?
          </h2>
          <p className="text-gray-400 mb-8">
            We offer comprehensive ServSafe training, CPR certification, and more professional development programs.
          </p>
          <Button 
            onClick={() => navigate('/training')}
            className="cta-button px-8 py-4 text-lg shadow-lg shadow-[#C9A227]/30"
            data-testid="services-training-btn"
          >
            View Training Programs
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-gray-400 mb-8">
            Contact us today to discuss how our services can help transform your hospitality business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/contact')}
              className="cta-button px-8 py-4"
              data-testid="services-contact-btn"
            >
              Contact Us
            </Button>
            <a
              href={`tel:${PHONE_NUMBER}`}
              className="outline-button px-8 py-4 rounded-md flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              {PHONE_NUMBER}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

// ============================================
// TRAINING PAGE
// ============================================
const TrainingPage = () => {
  const navigate = useNavigate();

  const trainingPrograms = [
    {
      icon: Shield,
      title: "ServSafe Food Handler",
      description: "Essential food safety training for all food service employees. Learn proper food handling, storage, and hygiene practices.",
      duration: "2-4 hours",
      certification: "ServSafe Food Handler Certificate"
    },
    {
      icon: Award,
      title: "ServSafe Food Protection Manager",
      description: "Advanced food safety certification for managers and supervisors. Covers HACCP principles, foodborne illness prevention, and regulatory compliance.",
      duration: "8 hours",
      certification: "ServSafe Manager Certificate"
    },
    {
      icon: AlertCircle,
      title: "Allergen Awareness Training",
      description: "Comprehensive training on food allergens, cross-contamination prevention, and serving guests with food allergies safely.",
      duration: "2-3 hours",
      certification: "Allergen Awareness Certificate"
    },
    {
      icon: Wine,
      title: "Alcohol Training",
      description: "Responsible alcohol service training covering legal requirements, identifying intoxication, and preventing underage sales.",
      duration: "2-4 hours",
      certification: "Responsible Alcohol Service Certificate"
    },
    {
      icon: Users,
      title: "Sexual Harassment Prevention Training",
      description: "Mandatory workplace training covering recognition, prevention, and proper response to sexual harassment in the workplace.",
      duration: "2-3 hours",
      certification: "Completion Certificate"
    },
    {
      icon: Heart,
      title: "CPR Certification",
      description: "Life-saving skills training including CPR, AED use, and basic first aid for workplace emergencies.",
      duration: "3-4 hours",
      certification: "CPR/AED Certificate"
    },
    {
      icon: Star,
      title: "AHEIL Customer Service Certified",
      description: "Excellence in hospitality customer service. Master the skills to provide exceptional guest experiences.",
      duration: "4-6 hours",
      certification: "AHEIL Customer Service Certificate"
    }
  ];

  return (
    <main className="pt-32" data-testid="training-page">
      <Helmet>
        <title>Training & Certifications | ServSafe, CPR, Food Safety | A2 Hospitality</title>
        <meta name="description" content="Professional food safety training and certifications including ServSafe Food Handler, ServSafe Manager, Allergen Awareness, Alcohol Training, CPR Certification, and AHEIL Customer Service. Certified ServSafe Proctors & Instructors." />
        <link rel="canonical" href="https://a2consulting.com/training" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Training & Certifications | A2 Hospitality & Consulting Services LLC" />
        <meta property="og:description" content="Professional development and certification programs - ServSafe, CPR, Allergen Awareness, and more. Certified ServSafe Proctors & Instructors." />
        <meta property="og:url" content="https://a2consulting.com/training" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_e9c33c6a-af4f-4988-bb48-d1ed87b9676f/artifacts/06bqp001_1.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Training & Certifications | A2 Hospitality & Consulting Services LLC" />
        <meta name="twitter:description" content="Professional development and certification programs - ServSafe, CPR, Allergen Awareness, and more." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_e9c33c6a-af4f-4988-bb48-d1ed87b9676f/artifacts/06bqp001_1.png" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 w-[600px] h-[600px] bg-[#C9A227]/10 rounded-full blur-[200px] transform -translate-x-1/2"></div>
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-[#C9A227]/10 px-4 py-2 rounded-full mb-6 border border-[#C9A227]/30">
            <GraduationCap className="w-5 h-5 text-[#C9A227]" />
            <span className="text-[#C9A227] font-medium">Certified ServSafe Proctors & Instructors</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Training & <span className="text-gold-gradient">Certifications</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto mb-8">
            Professional development and certification programs to ensure your team meets the highest industry standards in food safety, customer service, and workplace compliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/contact')}
              className="cta-button px-8 py-4 text-lg shadow-lg shadow-[#C9A227]/30"
              data-testid="training-request-btn"
            >
              Request Training
            </Button>
            <a
              href={`tel:${PHONE_NUMBER}`}
              className="outline-button px-8 py-4 text-lg rounded-md flex items-center justify-center gap-2"
              data-testid="training-call-btn"
            >
              <Phone className="w-5 h-5" />
              Book a Class
            </a>
          </div>
        </div>
      </section>

      {/* Training Programs Grid */}
      <section className="py-12 px-4 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Our Training Programs</h2>
            <p className="text-gray-400">Comprehensive certifications for hospitality professionals</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trainingPrograms.map((program, index) => (
              <Card3D key={index}>
                <Card 
                  className="training-card bg-[#1a1a1a] border-[#C9A227]/30 h-full flex flex-col"
                  data-testid={`training-card-${index}`}
                >
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#C9A227] to-[#E5C76B] flex items-center justify-center mb-4 shadow-lg shadow-[#C9A227]/30">
                      <program.icon className="w-6 h-6 text-black" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-white">{program.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <CardDescription className="text-gray-400 mb-4">{program.description}</CardDescription>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Clock className="w-4 h-4 text-[#C9A227]" />
                        <span>Duration: {program.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <Award className="w-4 h-4 text-[#C9A227]" />
                        <span>{program.certification}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Card3D>
            ))}
          </div>
        </div>
      </section>

      {/* Training in Action Video Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-[#C9A227] uppercase tracking-wider text-sm font-medium mb-2">Professional Training</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
                Learning in <span className="text-gold-gradient">Action</span>
              </h2>
              <p className="text-gray-400 mb-6">
                Our hands-on training programs combine theoretical knowledge with practical application. Watch our certified instructors guide students through real-world scenarios in professional kitchen environments.
              </p>
              <ul className="space-y-3 mb-6">
                {["Interactive demonstrations", "Hands-on practice sessions", "Real kitchen scenarios", "Expert feedback and guidance"].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-[#C9A227]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card3D className="col-span-2">
                <div className="relative aspect-video rounded-xl overflow-hidden border border-[#C9A227]/30 shadow-xl shadow-[#C9A227]/10">
                  <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                    <source src={VIDEO_6} type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <p className="text-white font-semibold">Professional Training</p>
                    <p className="text-gray-300 text-sm">Expert Instruction</p>
                  </div>
                </div>
              </Card3D>
              <Card3D>
                <div className="relative aspect-square rounded-xl overflow-hidden border border-[#C9A227]/30">
                  <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                    <source src={VIDEO_7} type="video/mp4" />
                  </video>
                </div>
              </Card3D>
              <Card3D>
                <div className="relative aspect-square rounded-xl overflow-hidden border border-[#C9A227]/30">
                  <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                    <source src={VIDEO_8} type="video/mp4" />
                  </video>
                </div>
              </Card3D>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
                Why Train With <span className="text-gold-gradient">A2 Hospitality</span>?
              </h2>
              <ul className="space-y-4">
                {[
                  { title: "Certified ServSafe Proctors & Instructors", desc: "Official certification from the National Restaurant Association" },
                  { title: "Flexible Scheduling", desc: "On-site training at your location or scheduled classes" },
                  { title: "Group & Individual Training", desc: "Programs tailored for teams of any size" },
                  { title: "Industry Expertise", desc: "Real-world experience from hospitality professionals" }
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 group">
                    <div className="w-6 h-6 rounded-full bg-[#C9A227]/20 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#C9A227]/40 transition-colors">
                      <CheckCircle className="w-4 h-4 text-[#C9A227]" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">{item.title}</p>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <Card3D>
              <div className="bg-gradient-to-br from-[#C9A227]/20 to-transparent p-8 rounded-xl border border-[#C9A227]/30">
                <h3 className="font-display text-2xl font-bold mb-4">Schedule Your Training</h3>
                <p className="text-gray-400 mb-6">
                  Ready to get your team certified? Contact us to schedule training sessions at your convenience.
                </p>
                <div className="space-y-4">
                  <a
                    href={`tel:${PHONE_NUMBER}`}
                    className="cta-button w-full py-4 rounded-md flex items-center justify-center gap-2 text-lg shadow-lg shadow-[#C9A227]/30"
                    data-testid="training-schedule-call-btn"
                  >
                    <Phone className="w-5 h-5" />
                    Call {PHONE_NUMBER}
                  </a>
                  <Button 
                    onClick={() => navigate('/contact')}
                    className="outline-button w-full py-4"
                    data-testid="training-contact-btn"
                  >
                    Request Training Online
                  </Button>
                </div>
              </div>
            </Card3D>
          </div>
        </div>
      </section>
    </main>
  );
};

// ============================================
// ABOUT PAGE
// ============================================
const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <main className="pt-32" data-testid="about-page">
      <Helmet>
        <title>About Us | A2 Hospitality & Consulting Services LLC</title>
        <meta name="description" content="Learn about A2 Hospitality & Consulting Services LLC - Certified ServSafe Proctors and Instructors dedicated to transforming hospitality establishments through expert guidance and innovative strategies." />
        <link rel="canonical" href="https://a2consulting.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About Us | A2 Hospitality & Consulting Services LLC" />
        <meta property="og:description" content="Dedicated to transforming hospitality establishments through expert guidance, innovative strategies, and a passion for culinary excellence." />
        <meta property="og:url" content="https://a2consulting.com/about" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_e9c33c6a-af4f-4988-bb48-d1ed87b9676f/artifacts/06bqp001_1.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Us | A2 Hospitality & Consulting Services LLC" />
        <meta name="twitter:description" content="Dedicated to transforming hospitality establishments through expert guidance, innovative strategies, and a passion for culinary excellence." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_e9c33c6a-af4f-4988-bb48-d1ed87b9676f/artifacts/06bqp001_1.png" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C9A227]/10 rounded-full blur-[200px]"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-display text-4xl md:text-5xl font-bold mb-6">
                Crafting <span className="text-gold-gradient">Excellence</span> in Hospitality
              </h1>
              <p className="text-gray-400 text-lg mb-6">
                A2 Hospitality & Consulting Services LLC is dedicated to transforming hospitality establishments through expert guidance, innovative strategies, and a passion for culinary excellence.
              </p>
              <p className="text-gray-400 mb-8">
                We partner with restaurants, hotels, and food service operations to deliver exceptional results. Our team brings decades of combined experience in the hospitality industry, understanding the unique challenges and opportunities that define success in this dynamic field.
              </p>
            </div>
            <div className="flex justify-center">
              <Card3D>
                <img 
                  src={LOGO_URL} 
                  alt="A2 Hospitality & Consulting Services LLC Logo" 
                  className="w-64 h-auto drop-shadow-[0_0_40px_rgba(201,162,39,0.4)]"
                  loading="lazy"
                />
              </Card3D>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">What Sets Us Apart</h2>
            <p className="text-gray-400">Our commitment to excellence in every aspect of hospitality consulting</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Star, title: "Years of Expertise", desc: "Decades of combined experience in the hospitality industry" },
              { icon: Award, title: "Proven Track Record", desc: "Consistent delivery of exceptional results for our clients" },
              { icon: Users, title: "Personalized Approach", desc: "Custom solutions tailored to your unique needs" },
              { icon: CheckCircle, title: "Commitment to Excellence", desc: "Unwavering dedication to the highest standards" }
            ].map((item, index) => (
              <Card3D key={index}>
                <Card className="bg-[#1a1a1a] border-[#C9A227]/20 text-center p-6 h-full">
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C9A227]/20 to-[#C9A227]/5 flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-8 h-8 text-[#C9A227]" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              </Card3D>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <Card3D>
              <div className="bg-gradient-to-br from-[#C9A227]/10 to-transparent p-8 rounded-xl border border-[#C9A227]/20">
                <h3 className="font-display text-2xl font-bold mb-6">Our Certifications</h3>
                <ul className="space-y-4">
                  {[
                    { icon: Shield, text: "Certified ServSafe Proctors" },
                    { icon: Award, text: "Certified ServSafe Instructors" },
                    { icon: Heart, text: "CPR/AED Certified Trainers" },
                    { icon: Star, text: "AHEIL Customer Service Certified" }
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <item.icon className="w-6 h-6 text-[#C9A227]" />
                      <span className="text-white">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card3D>
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
                Professional <span className="text-gold-gradient">Credentials</span>
              </h2>
              <p className="text-gray-400 mb-6">
                Our team holds industry-leading certifications that ensure we deliver the highest quality training and consulting services. As certified ServSafe Proctors and Instructors, we're authorized to provide official food safety certifications recognized nationwide.
              </p>
              <Button 
                onClick={() => navigate('/training')}
                className="cta-button px-6 py-3 shadow-lg shadow-[#C9A227]/30"
                data-testid="about-training-btn"
              >
                View Training Programs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Video & Media Section */}
      <section className="py-20 px-4 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#C9A227] uppercase tracking-wider text-sm font-medium mb-2">See The Difference</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Our Commitment in Action</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Main Video */}
            <Card3D className="md:col-span-2">
              <div className="relative h-80 rounded-xl overflow-hidden border border-[#C9A227]/20">
                <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                  <source src={VIDEO_3} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <p className="text-[#C9A227] font-medium">Excellence in Every Detail</p>
                  <p className="text-white text-2xl font-bold">Professional Standards</p>
                </div>
              </div>
            </Card3D>
            
            {/* Side Video */}
            <Card3D>
              <div className="relative h-80 rounded-xl overflow-hidden border border-[#C9A227]/20">
                <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                  <source src={VIDEO_4} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <p className="text-[#C9A227] font-medium">Expert Guidance</p>
                  <p className="text-white text-xl font-bold">Culinary Excellence</p>
                </div>
              </div>
            </Card3D>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
            Ready to Partner With Us?
          </h2>
          <p className="text-gray-400 mb-8">
            Let's discuss how we can help elevate your hospitality business to new heights.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/contact')}
              className="cta-button px-8 py-4 shadow-lg shadow-[#C9A227]/30"
              data-testid="about-contact-btn"
            >
              Get In Touch
            </Button>
            <a
              href={`tel:${PHONE_NUMBER}`}
              className="outline-button px-8 py-4 rounded-md flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              {PHONE_NUMBER}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

// ===============================
// CONTACT PAGE (NETLIFY + POPUP)
// ===============================

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
const NETLIFY_FORM_NAME = "contact";
const NETLIFY_HONEYPOT = "bot-field";

// URL-encode helper for Netlify Forms
const encodeNetlify = (data) => {
  const params = new URLSearchParams();
  Object.entries(data).forEach(([k, v]) => {
    params.append(k, v == null ? "" : String(v));
  });
  return params.toString();
};

// Submit helper (POST to current path so Netlify captures it in SPA routes)
const submitToNetlify = async (values) => {
  const payload = { "form-name": NETLIFY_FORM_NAME, ...values };

  const res = await fetch(window.location.pathname, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: encodeNetlify(payload),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Netlify submit failed: ${res.status} ${res.statusText} ${text}`.trim()
    );
  }
};

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    [NETLIFY_HONEYPOT]: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // "success" | "error" | null

  // Popup state (shadcn AlertDialog)
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogVariant, setDialogVariant] = useState("success"); // "success" | "error"

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
      [NETLIFY_HONEYPOT]: "",
    });
  };

  const openDialog = (variant) => {
    setDialogVariant(variant);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Honeypot bot trap: treat as success but do not submit
      if (formData[NETLIFY_HONEYPOT]) {
        setSubmitStatus("success");
        resetForm();
        openDialog("success");
        return;
      }

      await submitToNetlify(formData);

      setSubmitStatus("success");
      resetForm();
      openDialog("success");
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
      openDialog("error");
    } finally {
      setIsSubmitting(false);
      window.setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <main className="pt-32" data-testid="contact-page">
      <Helmet>
        <title>Contact Us | A2 Hospitality & Consulting Services LLC</title>
        <meta
          name="description"
          content="Contact A2 Hospitality & Consulting Services LLC at 845-208-9725. Request training, schedule consultations, or inquire about our hospitality services. Serving clients nationwide."
        />
        <link rel="canonical" href="https://a2consulting.com/contact" />
      </Helmet>

      {/* ✅ Hidden Netlify skeleton form (IMPORTANT for SPAs / build-time detection) */}
      <form
        name={NETLIFY_FORM_NAME}
        data-netlify="true"
        netlify-honeypot={NETLIFY_HONEYPOT}
        hidden
      >
        <input type="hidden" name="form-name" value={NETLIFY_FORM_NAME} />
        <input name={NETLIFY_HONEYPOT} />
        <input name="name" />
        <input name="email" />
        <input name="phone" />
        <input name="subject" />
        <textarea name="message" />
      </form>

      {/* ✅ Popup Confirmation Dialog */}
      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {dialogVariant === "success" ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Message Sent!
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  Submission Failed
                </>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {dialogVariant === "success"
                ? "Thanks! Your message was sent successfully. We’ll get back to you soon."
                : "Something went wrong submitting your message. Please try again, or call/email us directly."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hero */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Let's <span className="text-gold-gradient">Connect</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Ready to elevate your hospitality business? Contact us today to discuss how we can help
            you achieve excellence.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="font-display text-2xl font-bold mb-6">Contact Information</h2>

              <div className="space-y-6">
                {[
                  {
                    icon: Phone,
                    title: "Phone",
                    content: PHONE_NUMBER,
                    link: `tel:${PHONE_NUMBER}`,
                    note: "Click to call on mobile",
                  },
                  { icon: Mail, title: "Email", content: EMAIL, link: `mailto:${EMAIL}` },
                  { icon: MapPin, title: "Location", content: "Serving clients nationwide" },
                  {
                    icon: Clock,
                    title: "Business Hours",
                    hours: ["Monday - Sunday: 8:00 AM - 5:00 PM"],
                  },
                ].map((item, index) => (
                  <Card3D key={index}>
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-[#1a1a1a]/50 border border-[#C9A227]/10">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#C9A227] to-[#E5C76B] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#C9A227]/20">
                        <item.icon className="w-6 h-6 text-black" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                        {item.link ? (
                          <a href={item.link} className="text-[#C9A227] text-lg hover:underline">
                            {item.content}
                          </a>
                        ) : item.hours ? (
                          <div className="text-gray-400 text-sm space-y-1">
                            {item.hours.map((h, i) => (
                              <p key={i}>{h}</p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-400">{item.content}</p>
                        )}
                        {item.note && <p className="text-gray-500 text-sm mt-1">{item.note}</p>}
                      </div>
                    </div>
                  </Card3D>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <Card3D>
              <div className="bg-[#1a1a1a] p-8 rounded-xl border border-[#C9A227]/20">
                <h2 className="font-display text-2xl font-bold mb-6">Send Us a Message</h2>

                {/* Banner feedback */}
                {submitStatus === "success" && (
                  <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-3 text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    <span>Message sent successfully! We'll get back to you soon.</span>
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3 text-red-400">
                    <AlertCircle className="w-5 h-5" />
                    <span>Something went wrong. Please try again or call us directly.</span>
                  </div>
                )}

                {/* ✅ Netlify form */}
                <form
                  name={NETLIFY_FORM_NAME}
                  method="POST"
                  action="/contact"
                  data-netlify="true"
                  netlify-honeypot={NETLIFY_HONEYPOT}
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  data-testid="contact-form"
                >
                  <input type="hidden" name="form-name" value={NETLIFY_FORM_NAME} />

                  {/* honeypot */}
                  <p className="hidden">
                    <label>
                      Don’t fill this out:{" "}
                      <input
                        name={NETLIFY_HONEYPOT}
                        value={formData[NETLIFY_HONEYPOT]}
                        onChange={handleInputChange}
                      />
                    </label>
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <Input
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Subject *
                    </label>
                    <Input
                      name="subject"
                      type="text"
                      required
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="How can we help you?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your project or training needs..."
                    />
                  </div>

                  <Button type="submit" disabled={isSubmitting} className="w-full">
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </div>
            </Card3D>
          </div>
        </div>
      </section>
    </main>
  );
};


// ============================================
// PRIVACY POLICY PAGE
// ============================================
const PrivacyPolicyPage = () => {
  return (
    <main className="pt-32" data-testid="privacy-policy-page">
      <Helmet>
        <title>Privacy Policy | A2 Hospitality & Consulting Services LLC</title>
        <meta name="description" content="Privacy Policy for A2 Hospitality & Consulting Services LLC. Learn how we collect, use, and protect your personal information." />
        <link rel="canonical" href="https://a2consulting.com/privacy-policy" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Privacy Policy | A2 Hospitality & Consulting Services LLC" />
        <meta property="og:description" content="Privacy Policy for A2 Hospitality & Consulting Services LLC. Learn how we collect, use, and protect your personal information." />
        <meta property="og:url" content="https://a2consulting.com/privacy-policy" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_e9c33c6a-af4f-4988-bb48-d1ed87b9676f/artifacts/06bqp001_1.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | A2 Hospitality & Consulting Services LLC" />
        <meta name="twitter:description" content="Privacy Policy for A2 Hospitality & Consulting Services LLC." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_e9c33c6a-af4f-4988-bb48-d1ed87b9676f/artifacts/06bqp001_1.png" />
      </Helmet>
      
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-8 text-center">
            Privacy <span className="text-gold-gradient">Policy</span>
          </h1>
          
          <div className="prose prose-invert max-w-none space-y-8">
            <Card className="bg-[#1a1a1a] border-[#C9A227]/20 p-8">
              <p className="text-gray-400 mb-6">
                <strong className="text-white">Last Updated:</strong> July 2025
              </p>
              
              <p className="text-gray-300 mb-6">
                A2 Hospitality & Consulting Services LLC ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
              
              <h2 className="text-2xl font-semibold text-white mt-8 mb-4">1. Information We Collect</h2>
              <p className="text-gray-300 mb-4">We may collect information about you in various ways, including:</p>
              <ul className="list-disc pl-6 text-gray-300 space-y-2">
                <li><strong className="text-white">Personal Data:</strong> Name, email address, phone number, and other contact information you provide when filling out our contact form or requesting services.</li>
                <li><strong className="text-white">Business Information:</strong> Company name, job title, and business-related information relevant to our consulting services.</li>
                <li><strong className="text-white">Usage Data:</strong> Information about how you access and use our website, including your IP address, browser type, pages visited, and time spent on pages.</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-white mt-8 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-300 mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 text-gray-300 space-y-2">
                <li>Respond to your inquiries and provide customer service</li>
                <li>Process and manage your service requests</li>
                <li>Send you information about our services, training programs, and certifications</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-white mt-8 mb-4">3. Information Sharing</h2>
              <p className="text-gray-300 mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-300 space-y-2">
                <li>With service providers who assist us in operating our website and conducting our business</li>
                <li>When required by law or to protect our rights</li>
                <li>With your consent</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-white mt-8 mb-4">4. Data Security</h2>
              <p className="text-gray-300 mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
              
              <h2 className="text-2xl font-semibold text-white mt-8 mb-4">5. Your Rights</h2>
              <p className="text-gray-300 mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-gray-300 space-y-2">
                <li>Access and receive a copy of your personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Opt-out of marketing communications</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-white mt-8 mb-4">6. Cookies</h2>
              <p className="text-gray-300 mb-4">
                Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can set your browser to refuse cookies, but some features of our website may not function properly.
              </p>
              
              <h2 className="text-2xl font-semibold text-white mt-8 mb-4">7. Third-Party Links</h2>
              <p className="text-gray-300 mb-4">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
              
              <h2 className="text-2xl font-semibold text-white mt-8 mb-4">8. Children's Privacy</h2>
              <p className="text-gray-300 mb-4">
                Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children.
              </p>
              
              <h2 className="text-2xl font-semibold text-white mt-8 mb-4">9. Changes to This Policy</h2>
              <p className="text-gray-300 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
              </p>
              
              <h2 className="text-2xl font-semibold text-white mt-8 mb-4">10. Contact Us</h2>
              <p className="text-gray-300 mb-4">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="bg-[#0f0f0f] p-6 rounded-lg border border-[#C9A227]/20">
                <p className="text-gray-300 mb-2">
                  <strong className="text-[#C9A227]">A2 Hospitality & Consulting Services LLC</strong>
                </p>
                <p className="text-gray-300 mb-1">
                  <strong className="text-white">Phone:</strong>{" "}
                  <a href="tel:845-208-9725" className="text-[#C9A227] hover:underline">845-208-9725</a>
                </p>
                <p className="text-gray-300 mb-1">
                  <strong className="text-white">Email:</strong>{" "}
                  <a href="mailto:a2hospitalityandconsultingserv@gmail.com" className="text-[#C9A227] hover:underline">a2hospitalityandconsultingserv@gmail.com</a>
                </p>
                <p className="text-gray-300">
                  <strong className="text-white">Hours:</strong> Monday - Sunday: 8:00 AM - 5:00 PM
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

// ============================================
// SCROLL TO TOP COMPONENT
// ============================================
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// ============================================
// MAIN APP COMPONENT
// ============================================
function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-[#0a0a0a]">
        <BrowserRouter>
          <ScrollToTop />
          <Header />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/training" element={<TrainingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          </Routes>
          <Footer />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
